import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DashboardMeta } from "@/components/DashboardMeta";
import {
  Plus, MapPin, Calendar as CalIcon, Trash2, Edit3, ExternalLink,
  CheckCircle2, AlertTriangle, Search, ChevronLeft, ChevronRight, Bug,
} from "lucide-react";
import {
  format, parseISO, isToday, isSameMonth, isSameDay, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, addDays, addMonths, subMonths, isWithinInterval,
} from "date-fns";

const canWrite = (role) => role === "admin" || role === "pastoral";

function eventStart(ev) {
  const s = ev?.start || {};
  return s.dateTime || (s.date ? s.date + "T00:00:00" : null);
}
function eventEnd(ev) {
  const e = ev?.end || {};
  return e.dateTime || (e.date ? e.date + "T00:00:00" : null);
}
function isAllDay(ev) { return !!ev?.start?.date && !ev?.start?.dateTime; }

const emptyForm = {
  summary: "", description: "", location: "",
  date: "", startTime: "", endTime: "",
  all_day: false,
  calendar_id: "",
};

function toIsoLocal(date, time) {
  if (!date) return null;
  const d = new Date(`${date}T${time || "00:00"}:00`);
  return d.toISOString();
}
function fromEvent(ev) {
  const s = ev.start || {};
  const e = ev.end || {};
  if (s.date) {
    return {
      summary: ev.summary || "",
      description: ev.description || "",
      location: ev.location || "",
      date: s.date,
      startTime: "",
      endTime: "",
      all_day: true,
      calendar_id: ev._calendar_id || "",
      _endDate: e.date || s.date,
    };
  }
  const sd = new Date(s.dateTime);
  const ed = e.dateTime ? new Date(e.dateTime) : sd;
  const pad = (n) => String(n).padStart(2, "0");
  return {
    summary: ev.summary || "",
    description: ev.description || "",
    location: ev.location || "",
    date: `${sd.getFullYear()}-${pad(sd.getMonth() + 1)}-${pad(sd.getDate())}`,
    startTime: `${pad(sd.getHours())}:${pad(sd.getMinutes())}`,
    endTime: `${pad(ed.getHours())}:${pad(ed.getMinutes())}`,
    all_day: false,
    calendar_id: ev._calendar_id || "",
  };
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [today, setToday] = useState({ events: [], info: null });
  const [week, setWeek] = useState({ events: [], info: null });
  const [month, setMonth] = useState({ events: [], info: null });
  const [viewMonth, setViewMonth] = useState(() => new Date()); // first of month navigates
  const [selectedDay, setSelectedDay] = useState(null);         // Date
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [showDebug, setShowDebug] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const monthOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

  const [fetchedAt, setFetchedAt] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const loadAll = async () => {
    setLoadError(null);
    try {
      const s = await api.get("/calendar/google/status");
      setStatus(s.data);
      if (!s.data.connected) return;
      setLoading(true);
      const [t, w, m] = await Promise.all([
        api.get("/calendar/google/events?range=today"),
        api.get("/calendar/google/events?range=week"),
        api.get(`/calendar/google/events?month_of=${monthOf(viewMonth)}`),
      ]);
      const unpack = (r) => ({
        events: r.data.events || [],
        info: { ...r.data, events: undefined },
      });
      setToday(unpack(t));
      setWeek(unpack(w));
      setMonth(unpack(m));
      setFetchedAt(new Date().toISOString());
    } catch (e) {
      const detail = e?.response?.data?.detail || "Unable to load calendar";
      setLoadError(detail);
      if (e?.response?.status !== 401) toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  // Reload month data when view month changes
  useEffect(() => {
    (async () => {
      if (!status?.connected) return;
      try {
        setLoading(true);
        const m = await api.get(`/calendar/google/events?month_of=${monthOf(viewMonth)}`);
        setMonth({ events: m.data.events || [], info: { ...m.data, events: undefined } });
        setFetchedAt(new Date().toISOString());
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [viewMonth]);

  const filterQ = (arr) =>
    !q ? arr : arr.filter((e) => (e.summary || "").toLowerCase().includes(q.toLowerCase()));

  const todayEvents = useMemo(() => filterQ(today.events), [today, q]);
  const weekEvents = useMemo(() => filterQ(week.events), [week, q]);
  const monthEvents = useMemo(() => filterQ(month.events), [month, q]);

  // --- Month grid ---
  const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 0 });
  const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 0 });
  const gridDays = useMemo(() => {
    const days = [];
    let d = gridStart;
    while (d <= gridEnd) {
      days.push(d);
      d = addDays(d, 1);
    }
    return days;
  }, [gridStart, gridEnd]);

  const eventsByDay = useMemo(() => {
    const m = new Map();
    for (const ev of monthEvents) {
      const s = eventStart(ev);
      if (!s) continue;
      const d = parseISO(s);
      const key = format(d, "yyyy-MM-dd");
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(ev);
    }
    return m;
  }, [monthEvents]);

  const selectedEvents = useMemo(() => {
    if (!selectedDay) return [];
    const key = format(selectedDay, "yyyy-MM-dd");
    return eventsByDay.get(key) || [];
  }, [selectedDay, eventsByDay]);

  // --- Create / edit ---
  const openCreate = (prefillDate) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const base = prefillDate || now;
    const dateStr = `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}`;
    const startH = prefillDate ? 9 : now.getHours();
    const start = `${pad(startH)}:00`;
    const end = `${pad(startH + 1)}:00`;
    setEditing(null);
    setForm({ ...emptyForm, date: dateStr, startTime: start, endTime: end });
    setOpen(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({ ...emptyForm, ...fromEvent(ev) });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.summary.trim()) return toast.error("Title is required");
    if (!form.date) return toast.error("Pick a date");
    const body = {
      summary: form.summary,
      description: form.description || null,
      location: form.location || null,
      calendar_id: form.calendar_id || null,
      all_day: form.all_day,
    };
    if (form.all_day) {
      body.start = form.date;
      body.end = editing?._endDate || form.date;
    } else {
      body.start = toIsoLocal(form.date, form.startTime || "09:00");
      body.end = toIsoLocal(form.date, form.endTime || form.startTime || "10:00");
    }
    try {
      if (editing) {
        await api.patch(`/calendar/google/events/${editing.id}`, body);
        toast.success("Event updated");
      } else {
        await api.post("/calendar/google/events", body);
        toast.success("Event created");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to save event");
    }
  };

  const del = async (ev) => {
    if (!confirm(`Delete "${ev.summary || "(no title)"}"?`)) return;
    try {
      await api.delete(`/calendar/google/events/${ev.id}`, { params: { calendar_id: ev._calendar_id } });
      toast.success("Event deleted");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to delete");
    }
  };

  // ---------------- Render ----------------
  if (status && !status.connected) {
    return (
      <div className="space-y-6" data-testid="calendar-page-disconnected">
        <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-10 flex flex-col md:flex-row items-center gap-8">
          <div className="h-20 w-20 rounded-2xl bg-terracotta/10 grid place-items-center shrink-0">
            <CalIcon className="h-9 w-9 text-terracotta" strokeWidth={1.6} />
          </div>
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">Google Calendar</p>
            <h3 className="font-heading text-2xl text-brand-primary mt-1">Not connected yet</h3>
            <p className="text-brand-primary/70 mt-2 max-w-2xl">
              {user?.role === "admin"
                ? "Head to Settings to connect Google Calendar."
                : "Ask an admin to connect Google Calendar in Settings."}
            </p>
            {user?.role === "admin" && (
              <a href="/settings" className="inline-flex items-center gap-2 mt-5 bg-terracotta hover:bg-terracotta-hover text-white rounded-full px-5 py-2.5 font-medium transition-all">
                Open Settings <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        </Card>
      </div>
    );
  }

  const anyErrors = [
    ...(today.info?.errors || []),
    ...(week.info?.errors || []),
    ...(month.info?.errors || []),
  ];
  const scopeIssue = anyErrors.some((e) => (e.error || "").toLowerCase().includes("insufficient") || (e.error || "").includes("403"));

  return (
    <div className="space-y-8" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Badge className="rounded-full bg-sage/20 text-brand-primary border-0" data-testid="calendar-connected-badge">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
            Connected{status?.connected_email ? ` as ${status.connected_email}` : ""}
          </Badge>
          <Badge variant="outline" className="rounded-full border-brand-primary/15 text-brand-primary/70" data-testid="calendar-tz-badge">
            {status?.timezone || "America/Chicago"}
          </Badge>
          <DashboardMeta
            fetchedAt={fetchedAt}
            loading={loading}
            error={loadError}
            onRefresh={loadAll}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDebug((v) => !v)}
            data-testid="calendar-debug-toggle"
            className="text-brand-primary/70 hover:text-brand-primary"
          >
            <Bug className="h-4 w-4 mr-1.5" /> {showDebug ? "Hide debug" : "Debug"}
          </Button>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto flex-wrap">
          <div className="relative flex-1 sm:flex-initial min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-primary/50" />
            <Input
              data-testid="calendar-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search events…"
              className="pl-10 h-11 w-full sm:w-72 rounded-full bg-white border-brand-primary/10"
            />
          </div>
          {canWrite(user?.role) && (
            <Button
              data-testid="add-event-button"
              onClick={() => openCreate()}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-11 px-5 shadow-warm shrink-0"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New event
            </Button>
          )}
        </div>
      </div>

      {/* Error / scope banner */}
      {scopeIssue && (
        <Card className="rounded-2xl bg-peach/25 border border-terracotta/30 shadow-warm p-5 flex items-start gap-4" data-testid="calendar-scope-warning">
          <AlertTriangle className="h-5 w-5 text-terracotta mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-brand-primary">Google returned "insufficient authentication scopes".</p>
            <p className="text-sm text-brand-primary/75 mt-1">
              The connected token doesn't include the Calendar scope. Fix in two steps:
            </p>
            <ol className="list-decimal list-inside text-sm text-brand-primary/80 mt-2 space-y-1">
              <li>Google Cloud Console → <strong>APIs &amp; Services → OAuth consent screen → Data Access → Add or remove scopes</strong> → add <code className="bg-white px-1.5 py-0.5 rounded">https://www.googleapis.com/auth/calendar</code> → <strong>Update &amp; Save</strong>.</li>
              <li>Back in this app, go to <a className="underline text-terracotta" href="/settings">Settings → Integrations → Google Calendar</a>, click <strong>Disconnect</strong>, then <strong>Connect Google Calendar</strong> again. You'll see a Google consent screen asking to "See, edit, share, and permanently delete all the calendars…" — click <strong>Allow</strong>.</li>
            </ol>
          </div>
        </Card>
      )}

      {/* Debug panel */}
      {showDebug && (
        <Card className="rounded-2xl bg-brand-primary text-white shadow-warm-lg p-6 text-sm" data-testid="calendar-debug-panel">
          <div className="grid md:grid-cols-3 gap-6">
            <DebugBlock title="Today" info={today.info} events={today.events} />
            <DebugBlock title="This week" info={week.info} events={week.events} />
            <DebugBlock title={format(viewMonth, "MMMM yyyy")} info={month.info} events={month.events} />
          </div>
          <div className="mt-5 pt-5 border-t border-white/15 text-white/80">
            <p><strong>Connected as:</strong> {status?.connected_email || "—"}</p>
            <p><strong>Timezone:</strong> {status?.timezone}</p>
            <p className="truncate"><strong>Calendar IDs:</strong> {(status?.calendar_ids || []).join(", ") || "—"}</p>
            {anyErrors.length > 0 && (
              <div className="mt-2">
                <p className="text-peach font-semibold">Errors:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {anyErrors.map((e, i) => <li key={i} className="text-white/90"><span className="opacity-60">{e.calendar_id}:</span> {e.error}</li>)}
                </ul>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Today */}
      <section data-testid="today-section">
        <div className="flex items-baseline gap-3 mb-4">
          <h3 className="font-heading text-2xl font-semibold text-brand-primary">Today's Events</h3>
          <p className="text-sm text-brand-primary/60">{format(new Date(), "EEEE, MMMM d")}</p>
          <Badge className="rounded-full bg-terracotta/10 text-terracotta border-0 ml-1">{todayEvents.length}</Badge>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {todayEvents.length === 0 && (
            <Card className="md:col-span-2 rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6 text-brand-primary/60">
              Nothing on the books for today.
            </Card>
          )}
          {todayEvents.map((e) => (
            <EventCard key={e.id + (e._calendar_id || "")} ev={e} canWrite={canWrite(user?.role)} onEdit={openEdit} onDelete={del} />
          ))}
        </div>
      </section>

      {/* This week */}
      <section data-testid="week-section">
        <div className="flex items-baseline gap-3 mb-4">
          <h3 className="font-heading text-2xl font-semibold text-brand-primary">This Week's Events</h3>
          {week.info && (
            <p className="text-sm text-brand-primary/60">
              {format(parseISO(week.info.from_local), "MMM d")} – {format(addDays(parseISO(week.info.to_local), -1), "MMM d")}
            </p>
          )}
          <Badge className="rounded-full bg-terracotta/10 text-terracotta border-0 ml-1">{weekEvents.length}</Badge>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {weekEvents.length === 0 && (
            <Card className="md:col-span-2 rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6 text-brand-primary/60">
              No events scheduled this week.
            </Card>
          )}
          {weekEvents.map((e) => (
            <EventCard key={e.id + (e._calendar_id || "")} ev={e} canWrite={canWrite(user?.role)} onEdit={openEdit} onDelete={del} highlightToday />
          ))}
        </div>
      </section>

      {/* Full month grid */}
      <section data-testid="month-section" className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setViewMonth((d) => subMonths(d, 1))} data-testid="month-prev" className="rounded-full border-brand-primary/15 h-9 w-9">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h3 className="font-heading text-xl sm:text-2xl font-semibold text-brand-primary min-w-[160px] sm:min-w-[240px] text-center">
              {format(viewMonth, "MMMM yyyy")}
            </h3>
            <Button variant="outline" size="icon" onClick={() => setViewMonth((d) => addMonths(d, 1))} data-testid="month-next" className="rounded-full border-brand-primary/15 h-9 w-9">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setViewMonth(new Date()); setSelectedDay(new Date()); }} data-testid="month-today" className="ml-2 text-brand-primary/70">
              Today
            </Button>
          </div>
          <p className="text-sm text-brand-primary/60">{monthEvents.length} events in view</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calendar grid */}
          <Card className="lg:col-span-2 rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-4" data-testid="month-grid">
            {/* Weekday header */}
            <div className="grid grid-cols-7 text-xs uppercase tracking-wider text-brand-primary/55 font-semibold px-1 pb-2">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                <div key={d} className="text-center py-2">{d}</div>
              ))}
            </div>
            {/* Days */}
            <div className="grid grid-cols-7 gap-1">
              {gridDays.map((day) => {
                const key = format(day, "yyyy-MM-dd");
                const items = eventsByDay.get(key) || [];
                const inMonth = isSameMonth(day, viewMonth);
                const today_ = isToday(day);
                const selected = selectedDay && isSameDay(day, selectedDay);
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedDay(day)}
                    onDoubleClick={() => canWrite(user?.role) && openCreate(day)}
                    data-testid={`day-cell-${key}`}
                    className={`relative min-h-[96px] rounded-xl border text-left p-2 transition-all duration-200 ${
                      !inMonth ? "bg-cream/60 text-brand-primary/35 border-transparent" : "bg-white border-brand-primary/10 hover:border-terracotta/40"
                    } ${selected ? "ring-2 ring-terracotta/60 border-terracotta/60" : ""} ${today_ ? "bg-terracotta/5" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${today_ ? "text-terracotta" : ""}`}>{format(day, "d")}</span>
                      {items.length > 0 && (
                        <span className="text-[10px] rounded-full bg-terracotta/15 text-terracotta px-1.5 py-0.5 font-semibold">{items.length}</span>
                      )}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {items.slice(0, 2).map((ev) => (
                        <div
                          key={ev.id + (ev._calendar_id || "")}
                          className="text-[11px] leading-tight px-1.5 py-0.5 rounded-md bg-sage/25 text-brand-primary truncate"
                          title={ev.summary}
                        >
                          {isAllDay(ev) ? "● " : `${format(parseISO(eventStart(ev)), "h:mma").toLowerCase()} `}
                          {ev.summary || "(no title)"}
                        </div>
                      ))}
                      {items.length > 2 && (
                        <div className="text-[10px] text-brand-primary/55 px-1.5">+{items.length - 2} more</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-brand-primary/50 mt-3 px-1">
              Click a day to see all events. {canWrite(user?.role) && "Double-click a day to quickly create a new event."}
            </p>
          </Card>

          {/* Selected day panel */}
          <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6 h-fit" data-testid="selected-day-panel">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">
                  {selectedDay ? format(selectedDay, "EEEE") : "Pick a day"}
                </p>
                <h3 className="font-heading text-xl text-brand-primary mt-0.5">
                  {selectedDay ? format(selectedDay, "MMMM d") : format(viewMonth, "MMMM yyyy")}
                </h3>
              </div>
              {selectedDay && canWrite(user?.role) && (
                <Button
                  size="sm"
                  onClick={() => openCreate(selectedDay)}
                  data-testid="day-add-event"
                  className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              )}
            </div>
            {!selectedDay ? (
              <p className="text-sm text-brand-primary/60">Click any day in the grid to see events scheduled that day.</p>
            ) : selectedEvents.length === 0 ? (
              <p className="text-sm text-brand-primary/60">Nothing scheduled.</p>
            ) : (
              <ul className="space-y-3" data-testid="selected-day-events">
                {selectedEvents.map((ev) => (
                  <li key={ev.id + (ev._calendar_id || "")} className="rounded-xl border border-brand-primary/10 p-3 hover:border-terracotta/30 transition">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-brand-primary truncate">{ev.summary || "(no title)"}</p>
                        <p className="text-xs text-brand-primary/60 mt-0.5">
                          {isAllDay(ev)
                            ? "All day"
                            : `${format(parseISO(eventStart(ev)), "h:mm a")}${eventEnd(ev) ? ` – ${format(parseISO(eventEnd(ev)), "h:mm a")}` : ""}`}
                        </p>
                        {ev.location && (
                          <p className="text-xs text-brand-primary/55 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.location}</p>
                        )}
                      </div>
                      {canWrite(user?.role) && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => openEdit(ev)} data-testid={`day-edit-${ev.id}`} className="text-brand-primary/40 hover:text-terracotta p-1">
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => del(ev)} data-testid={`day-delete-${ev.id}`} className="text-brand-primary/40 hover:text-terracotta p-1">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </section>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit event" : "New event"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input data-testid="event-title-input" placeholder="Event title" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
            <div>
              <label className="text-xs text-brand-primary/60 uppercase tracking-wider">Date</label>
              <Input data-testid="event-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <label className="flex items-center gap-3 text-sm text-brand-primary">
              <Switch data-testid="event-allday-switch" checked={form.all_day} onCheckedChange={(v) => setForm({ ...form, all_day: v })} />
              All day
            </label>
            {!form.all_day && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-brand-primary/60 uppercase tracking-wider">Starts</label>
                  <Input data-testid="event-start-input" type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-brand-primary/60 uppercase tracking-wider">Ends</label>
                  <Input data-testid="event-end-input" type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
                </div>
              </div>
            )}
            <Input data-testid="event-location-input" placeholder="Location (optional)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <Textarea data-testid="event-description-input" placeholder="Description (optional)" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <DialogFooter>
            <Button data-testid="event-submit-button" onClick={submit} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white">
              {editing ? "Save changes" : "Create event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DebugBlock({ title, info, events }) {
  return (
    <div>
      <p className="text-peach text-[11px] uppercase tracking-[0.15em] font-semibold">{title}</p>
      <p className="text-white/80 mt-1">
        <strong>Count:</strong> {events?.length ?? 0}
      </p>
      {info && (
        <p className="text-white/60 text-xs mt-0.5">
          {info.from_local ? format(parseISO(info.from_local), "MMM d h:mma") : "?"}
          {" → "}
          {info.to_local ? format(parseISO(info.to_local), "MMM d h:mma") : "?"}
        </p>
      )}
      <ul className="mt-2 space-y-0.5 max-h-40 overflow-auto">
        {(events || []).slice(0, 10).map((e) => {
          const s = eventStart(e);
          return (
            <li key={e.id} className="text-white/90 text-xs truncate">
              {s ? format(parseISO(s), "MMM d h:mma") : "?"} — {e.summary || "(no title)"}
            </li>
          );
        })}
        {(events || []).length === 0 && <li className="text-white/50 text-xs">(no events)</li>}
      </ul>
    </div>
  );
}

function EventCard({ ev, canWrite, onEdit, onDelete, highlightToday }) {
  const startStr = eventStart(ev);
  const endStr = eventEnd(ev);
  const start = startStr ? parseISO(startStr) : null;
  const end = endStr ? parseISO(endStr) : null;
  const allDay = isAllDay(ev);
  const today = start ? isToday(start) : false;
  return (
    <Card
      data-testid={`event-card-${ev.id}`}
      className={`rounded-2xl bg-white border shadow-warm p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg ${
        highlightToday && today ? "border-terracotta/40 ring-1 ring-terracotta/15" : "border-brand-primary/10"
      }`}
    >
      <div className="flex items-start gap-5">
        <div className="w-16 text-center shrink-0 rounded-xl py-2 border border-brand-primary/10">
          <p className="text-[11px] uppercase tracking-wider text-terracotta font-semibold">
            {start ? format(start, "MMM") : "--"}
          </p>
          <p className="font-heading text-2xl text-brand-primary -mt-0.5">
            {start ? format(start, "d") : "--"}
          </p>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <p className="font-heading text-lg font-medium text-brand-primary">{ev.summary || "(no title)"}</p>
            <div className="flex items-center gap-1 shrink-0">
              {canWrite && (
                <>
                  <button data-testid={`edit-event-${ev.id}`} onClick={() => onEdit(ev)} className="text-brand-primary/40 hover:text-terracotta p-1 transition">
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button data-testid={`delete-event-${ev.id}`} onClick={() => onDelete(ev)} className="text-brand-primary/40 hover:text-terracotta p-1 transition">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )}
              {ev.htmlLink && (
                <a href={ev.htmlLink} target="_blank" rel="noreferrer" data-testid={`open-event-${ev.id}`} className="text-brand-primary/40 hover:text-terracotta p-1 transition">
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-brand-primary/65 mt-1 flex-wrap">
            <span>
              {start
                ? (allDay
                    ? `${format(start, "EEE, MMM d")} · All day`
                    : `${format(start, "EEE, MMM d • h:mm a")}${end ? ` – ${format(end, "h:mm a")}` : ""}`)
                : "No start time"}
            </span>
            {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {ev.location}</span>}
          </div>
          {today && highlightToday && (
            <Badge className="mt-3 rounded-full bg-terracotta/15 text-terracotta border-0">Today</Badge>
          )}
          {ev.description && <p className="text-sm text-brand-primary/70 mt-3 whitespace-pre-line">{ev.description}</p>}
        </div>
      </div>
    </Card>
  );
}
